import { Delete, EditNote } from "@mui/icons-material";
import clsx from "clsx";
import { TFunction } from "i18next";
import { escapeRegExp } from "lodash";
import { FC, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import {
  BlockHighlight,
  FramerTooltip,
  StatusIndicatorBooking,
} from "@ryde/controls";
import {
  useBookingsActions,
  useBookingsNavigation,
  useDropdownSourceContext,
} from "@ryde/redux";
import {
  BookingDto,
  BookingStatusEnum,
  DirectionEnum,
  HubAssignmentSourceEnum,
  IExtendableDto,
  isHighlightedAfterEditSF,
  isIncompleteHubPairingBooking,
  isValidPositiveNumber,
  SimpleResult,
  systemSettingsDtoAtom,
  tableSearchAF,
  useDrawers,
  useFormats,
} from "@ryde/shared";

import { RideIdLink } from "./booking-calendar.ride-id-link";
import { CalendarBookingTooltipContentStyled } from "./booking-calendar.styled";
import { getCalendarBookingDirectionTooltipKey } from "./booking-calendar.table-body.cell.booking.utils";

const STATUS_INDICATOR_BOOKING_MOUTH_LEAVE_DELAY = 300;
const STATUS_INDICATOR_BOOKING_Y_OFFSET = 5;

interface IResolveStatusTooltipArguments {
  t: TFunction<"translation", undefined>;
  rideId: number | undefined;
  rideMainId: number | undefined;
  ridePersonalId: number | undefined;
  openRideEditFormDrawer: ({
    id,
  }: {
    id?: number;
  }) => Promise<SimpleResult<IExtendableDto>>;
  status: BookingStatusEnum;
  fromStation?: string;
  toStation?: string;
  hubDisplayName?: string;
  hubSource?: HubAssignmentSourceEnum;
}

const resolveCalendarHubTooltipLine = ({
  t,
  hubDisplayName,
  hubSource,
}: {
  t: TFunction<"translation", undefined>;
  hubDisplayName: string;
  hubSource?: HubAssignmentSourceEnum;
}): string => {
  const hubLine = t("booking.CALENDAR_TOOLTIP_HUB", {
    hubName: hubDisplayName,
  });

  if (hubSource === HubAssignmentSourceEnum.Calculated) {
    return `${hubLine} (${t("hub.calculatedHub")})`;
  }

  return hubLine;
};

const resolveStatusTooltip = ({
  t,
  status,
  openRideEditFormDrawer,
  rideId,
  rideMainId,
  ridePersonalId,
  fromStation,
  toStation,
  hubDisplayName,
  hubSource,
}: IResolveStatusTooltipArguments): ReactNode => {
  const fromTo = (
    <>
      {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_FROM", { fromStation })}
      <br />
      {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_TO", { toStation })}
      {!!hubDisplayName && (
        <>
          <br />
          {resolveCalendarHubTooltipLine({
            t,
            hubDisplayName,
            hubSource,
          })}
        </>
      )}
      <br />
    </>
  );

  const isRouted = status === BookingStatusEnum.Routed;
  const hasRideLinks =
    isRouted && (!!rideId || !!rideMainId || !!ridePersonalId);

  if (!hasRideLinks) {
    return <span>{fromTo}</span>;
  }

  return (
    <CalendarBookingTooltipContentStyled>
      {fromTo}
      {!!rideId && (
        <span>
          {t("booking.CALENDAR_ROUTED_STATUS_TOOLTIP_RIDE")}{" "}
          <RideIdLink
            id={rideId}
            openRideEditFormDrawer={openRideEditFormDrawer}
          />
          <br />
        </span>
      )}
      {!!rideMainId && (
        <span>
          {t("booking.CALENDAR_TOOLTIP_MAIN_HUB_RIDE")}{" "}
          <RideIdLink
            id={rideMainId}
            openRideEditFormDrawer={openRideEditFormDrawer}
          />
          <br />
        </span>
      )}
      {!!ridePersonalId && (
        <span>
          {t("booking.CALENDAR_TOOLTIP_PERSONAL_HUB_RIDE")}{" "}
          <RideIdLink
            id={ridePersonalId}
            openRideEditFormDrawer={openRideEditFormDrawer}
          />
        </span>
      )}
    </CalendarBookingTooltipContentStyled>
  );
};

interface IBookingCalendarTableBodyCellBooking {
  dto: BookingDto;
}

export const BookingCalendarTableBodyCellBooking: FC<
  IBookingCalendarTableBodyCellBooking
> = ({ dto }): JSX.Element => {
  const { t } = useTranslation();
  const searchValue = useRecoilValue(tableSearchAF("booking"));
  const { archiveBooking } = useBookingsActions();
  const { navigateBookingsEdit } = useBookingsNavigation();
  const isHighlight = useRecoilValue(
    isHighlightedAfterEditSF(["booking", dto.id]),
  );
  const isBookingRouted = dto.status === BookingStatusEnum.Routed;
  const isSearchHighlight = searchValue?.length
    ? String(dto.id).match(escapeRegExp(searchValue)) !== null
    : false;

  const { formatTimeNormalized } = useFormats();
  const { getDropdownLabel } = useDropdownSourceContext();
  const [mealName, setMealName] = useState<string>("");

  useEffect(() => {
    const fetchMealName = async (): Promise<void> => {
      if (dto.mealId) {
        try {
          const label = await getDropdownLabel("meal", dto.mealId);
          setMealName(label);
        } catch {
          setMealName("");
        }
      } else {
        setMealName("");
      }
    };

    fetchMealName();
  }, [dto.mealId, getDropdownLabel]);

  const directionTooltipContent = t(
    getCalendarBookingDirectionTooltipKey(dto.direction),
  );
  const { openRideEditFormDrawer } = useDrawers();
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const isHubsLogicEnabled = Boolean(systemSettings?.metadata?.enableHubsLogic);
  const hubDisplay = dto.computedData?.hubDisplay;

  // Backend may resolve hub from stored hubId or station settings (hubDisplay.effectiveHubId).
  // When computed data is absent, fall back to the raw stored hubId on the booking DTO.
  const hasComputedEffectiveHubId = hubDisplay?.effectiveHubId !== undefined;
  const resolvedHubId = hasComputedEffectiveHubId
    ? hubDisplay.effectiveHubId
    : dto.hubId;
  const hasAssignedHub = isValidPositiveNumber(resolvedHubId);

  // Hub station assignment: show hub icon when hubs feature is enabled and a hub is linked.
  const isHubBooking = isHubsLogicEnabled && hasAssignedHub;

  // Routed hub legs: show hub icon even without hub station when main/personal rides exist.
  const hasHubMainRide = !!dto.rideMainId;
  const hasHubPersonalRide = !!dto.ridePersonalId;
  const hasHubRideLinks =
    isHubsLogicEnabled && (hasHubMainRide || hasHubPersonalRide);
  const isIncompletePairing =
    isHubsLogicEnabled && isIncompleteHubPairingBooking(dto);

  const shouldShowHubIcon = isHubBooking || hasHubRideLinks;

  const resolveHubDisplayName = (): string | undefined => {
    const computedHubStationName = hubDisplay?.effectiveHubStationName;
    if (computedHubStationName) {
      return computedHubStationName;
    }

    const legacyHubStationName = dto.hubStationName;
    if (legacyHubStationName) {
      return legacyHubStationName;
    }

    const hubIdForLabel = hubDisplay?.effectiveHubId ?? dto.hubId;
    return isValidPositiveNumber(hubIdForLabel)
      ? String(hubIdForLabel)
      : undefined;
  };
  const hubDisplayName = resolveHubDisplayName();

  const statusTooltipContent = resolveStatusTooltip({
    rideId: dto.rideId,
    rideMainId: dto.rideMainId,
    ridePersonalId: dto.ridePersonalId,
    t,
    openRideEditFormDrawer,
    status: dto.status,
    fromStation: dto.originStationName,
    toStation: dto.destinationStationName,
    hubDisplayName,
    hubSource: hubDisplay?.hubSource,
  });

  const handleArchiveClick = useEvent(() => {
    if (isBookingRouted) {
      return;
    }
    archiveBooking(dto.id);
  });

  const resultToRender = (
    <div
      className={clsx("cell_row", {
        cell_row__highlight: isHighlight,
      })}
      data-e2e="calendar_booking_row"
      data-e2e-id={dto.id}
    >
      <FramerTooltip content={directionTooltipContent}>
        <span className="direction_cell">
          {dto.direction === DirectionEnum.PickUp && (
            <SVG_MUI.OvalTo2SVG data-e2e="svg-oval-to" />
          )}
          {/* RYDE-8171: direction X is a meal-only booking */}
          {dto.direction === DirectionEnum.None && (
            <SVG_MUI.OvalMeal2SVG data-e2e="svg-oval-meal-only" />
          )}
          {![DirectionEnum.PickUp, DirectionEnum.None].includes(
            dto.direction,
          ) && <SVG_MUI.OvalBack2SVG data-e2e="svg-oval-back" />}
        </span>
      </FramerTooltip>
      <span className="time_cell">{formatTimeNormalized(dto.bookTime)}</span>
      <FramerTooltip
        content={statusTooltipContent}
        offset={[0, STATUS_INDICATOR_BOOKING_Y_OFFSET]}
        mouseLeaveDelay={STATUS_INDICATOR_BOOKING_MOUTH_LEAVE_DELAY}
      >
        <span className="status_cell">
          <StatusIndicatorBooking status={dto.status} />
        </span>
      </FramerTooltip>
      {shouldShowHubIcon && (
        <FramerTooltip
          content={
            isIncompletePairing
              ? t("hub.incompletePairing")
              : statusTooltipContent
          }
          offset={[0, STATUS_INDICATOR_BOOKING_Y_OFFSET]}
          mouseLeaveDelay={STATUS_INDICATOR_BOOKING_MOUTH_LEAVE_DELAY}
        >
          <span
            className={clsx("hub_cell", {
              hub_cell__incomplete: isIncompletePairing,
            })}
            data-e2e={
              isIncompletePairing
                ? "calendar-hub-incomplete-pairing"
                : "calendar-hub-icon"
            }
          >
            {isBookingRouted ? (
              <SVG_MUI.HubRouted4SVG data-e2e="svg-hub-icon" />
            ) : (
              <SVG_MUI.HubNotRouted4SVG data-e2e="svg-hub-icon" />
            )}
            {isIncompletePairing && (
              <SVG_MUI.TriangleExclamationSVG
                className="hub_cell__incomplete-icon"
                data-e2e="svg-hub-incomplete"
              />
            )}
          </span>
        </FramerTooltip>
      )}
      {dto.mealId && (
        <FramerTooltip content={mealName}>
          <span className="meal_cell">
            <SVG_MUI.MealsIconSVG data-e2e="svg-meal-icon" />
          </span>
        </FramerTooltip>
      )}
      <span className="actions_cell">
        <EditNote
          className="edit-booking"
          onClick={() => navigateBookingsEdit(dto.id)}
          data-e2e="svg-edit"
        />
        <Delete
          className={clsx("delete-booking", {
            deleteBookingDisabled: isBookingRouted,
          })}
          data-e2e="svg-trash"
          onClick={handleArchiveClick}
        />
      </span>
    </div>
  );

  if (isSearchHighlight) {
    return <BlockHighlight>{resultToRender}</BlockHighlight>;
  }

  return resultToRender;
};
